import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactSVG } from "react-svg";
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import moment from 'moment';
import messages from '../messages';

import { useTheme } from '@mui/material/styles';
import { Box, Button, Typography, Checkbox, Tooltip, Modal, IconButton, TextField } from '@mui/material';
import { getStyles } from './styles';
import GeneralTable from '../../../../components/GeneralTable';
import { IconSvg } from '../../../../utils/globalIcons';
import { tostAlert } from '../../../../utils/AlertToast';
import LoadBar from '../../../../utils/LoadBar';
import { useAppContext } from '../../../../context/AppContext';
import AnalyticsApi from '../../../../Api/Services/AmeyaBackend/AnalyticsToolApi';
import getAnalyticsDataSourceData from '../../../../utils/ApiFunctions/AnalyticsDataSourceData';
import { setAnalyticsDataSourcesState } from '../../../../Redux/slice/dataSlice';
import { setAnalyticsDataSourcesAdded } from '../../../../Redux/slice/newDataSlice';
import AnalyticsDataSourcesApi from '../../../../Api/Services/AppflyteBackend/AnalyticsDataSources';
import { getMainStyles } from '../../../../styles/styles';
import { Close } from '@mui/icons-material';
import { fetchSubscriptionId, getUserName } from '../../../../utils/GetAccountDetails';

function AnalyticsIntegrations() {
    const theme = useTheme();
    const styles = getStyles(theme);
    const mainStyles = getMainStyles(theme);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const abortRef = useRef(false);
    const intl = useIntl();
    const { selectedProject } = useAppContext();

    const [loading, setLoading] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [connectors, setConnectors] = useState([])
    const [processingDbs, setProcessingDbs] = useState([]);

    const modalDetails = { modalstatus: false, integration_data: null };
    const [modalData, setModalData] = useState(modalDetails);

    const analytics_data_sources = useSelector(state => state.all_data.analytics_data_sources);
    const analytics_data_source_added = useSelector(state => state.data_added.analytics_data_source_added);

    useEffect(() => {
        getAllData();
    }, [analytics_data_source_added])

    const getAllData = async () => {
        setLoading(true)
        try {
            let all_sources = []
            if ((analytics_data_sources || [])?.length > 0 && analytics_data_source_added === false) {
                all_sources = analytics_data_sources
            } else {
                const response = await getAnalyticsDataSourceData(selectedProject?.payload?.__auto_id__);
                dispatch(setAnalyticsDataSourcesState(response))
                dispatch(setAnalyticsDataSourcesAdded(false))
                all_sources = response
            }

            const mongo_sources = (all_sources || [])?.filter(item => (item?.payload?.source_type === "mongodb" || item?.payload?.source_type === "ameya_extract")
                && item?.payload?.status === "in_progress") ?? [];
            setProcessingDbs(mongo_sources)
            setConnectors(all_sources)

        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if ((processingDbs || []).length > 0) {
            const cleanups = runChecks();
            return () => {
                abortRef.current = true;
                cleanups.forEach(cleanup => cleanup());
            };
        }
    }, [processingDbs]);

    const pollJobStatus = (jobId, interval = 5000, onUpdate) => {
        let timeoutId;

        const poll = async () => {
            if (abortRef.current) {
                clearTimeout(timeoutId);
                onUpdate?.({ status: "failed", message: "Polling aborted" });
                return;
            }

            try {
                const response = await AnalyticsApi.getJobStatus(jobId, {
                    validateStatus: () => true,
                });
                const data = response?.data ?? {};
                const status = data?.status ?? "error";
                const error_status = data?.error ?? null;
                const progress = data?.progress ?? null;
                console.log("progress")

                if (response.status === 200) {
                    if (status === "complete" && error_status === null) {
                        onUpdate?.({ status: "completed", ...data });
                    } else if (status === "error" || error_status !== null) {
                        onUpdate?.({ status: "failed", ...data });
                    } else {
                        onUpdate?.({ status: "pending", ...data });
                        timeoutId = setTimeout(poll, interval);
                    }
                } else {
                    onUpdate?.({ status: "failed", message: `HTTP ${response.status}` });
                }
            } catch (err) {
                onUpdate?.({ status: "failed", message: err.message });
            }
        };
        poll();
        return () => clearTimeout(timeoutId);
    };

    const runChecks = () => {
        const cleanups = [];
        try {
            for (const item of processingDbs || []) {

                const mongoIndexJobId = item?.payload?.mongoIndexJobId ?? null;
                const mongoFieldIndexJobId = item?.payload?.mongoFieldIndexJobId ?? null;

                if (mongoIndexJobId && mongoFieldIndexJobId) {
                    cleanups.push(
                        pollJobStatus(mongoIndexJobId, 7000, (mongoIndexData) => {
                            updateConnectorStatus(item, mongoIndexData, "mongoIndexProgress");
                        })
                    );

                    cleanups.push(
                        pollJobStatus(mongoFieldIndexJobId, 7000, (mongoFieldIndexData) => {
                            updateConnectorStatus(item, mongoFieldIndexData, "mongoFieldIndexProgress");
                        })
                    );
                }
            }
        } catch (err) {
            if (!abortRef.current && err.message !== "Polling aborted") {
                console.error(err);
            }
        }
        return cleanups;
    };

    const updateConnectorStatus = async (item, jobData, progressKey) => {
        const jobStatus = jobData.status
        const item_id = item?.payload?.__auto_id__ ?? null;

        if (!abortRef.current) {
            let shouldUpdateApi = false;
            let finalStatus = null;

            setConnectors(prev => {
                const connectors_data = [...prev];
                const index = connectors_data.findIndex(c => c?.payload?.__auto_id__ === item_id);
                if (index !== -1) {
                    const currentItem = connectors_data?.find(connector => connector?.payload?.__auto_id__ === item_id);
                    if (jobStatus === "completed") {
                        connectors_data[index] = {
                            ...currentItem,
                            [progressKey]: 100,
                            [`${progressKey}Status`]: "completed"
                        };

                        const updatedItem = connectors_data[index];
                        const mongoIndexCompleted = updatedItem.mongoIndexProgressStatus === "completed";
                        const mongoFieldIndexCompleted = updatedItem.mongoFieldIndexProgressStatus === "completed";

                        if (mongoIndexCompleted && mongoFieldIndexCompleted) {
                            connectors_data[index] = { ...updatedItem, status: "connected" };
                            shouldUpdateApi = true;
                            finalStatus = "connected";
                        }
                    } else if (jobStatus === "failed") {
                        connectors_data[index] = {
                            ...currentItem,
                            status: "failed",
                            [`${progressKey}Status`]: "failed"
                        };
                        shouldUpdateApi = true;
                        finalStatus = "failed";
                    } else if (jobStatus === "pending") {
                        connectors_data[index] = {
                            ...currentItem,
                            status: "in_progress",
                            [progressKey]: jobData.progress || 0,
                        };
                    }
                }
                return connectors_data;
            });

            if (shouldUpdateApi && finalStatus) {
                const response = await handleMongoSettingsUpdate(finalStatus, item);
                if (response) {
                    setProcessingDbs(prev => prev.filter(processdb => processdb?.payload?.__auto_id__ !== item_id));
                }
            }
        }
    };

    const handleMongoSettingsUpdate = async (status, item) => {
        try {
            const item_id = item?.payload?.__auto_id__ ?? null;
            const update_key = item?.update_key ?? null;
            const updateObj = {
                "id": item_id,
                "fields": [{
                    "path": "$.status",
                    "value": status
                }]
            }
            const update_response = await AnalyticsDataSourcesApi.update(JSON.stringify(updateObj), item_id, update_key);
            if (update_response.status === 200) {
                dispatch(setAnalyticsDataSourcesAdded(true))
                return true
            }
            return null
        } catch (error) {
            console.log(error)
            return null
        }
    }

    const handleIntegrationsDelete = async (source_data) => {

        if (!source_data) {
            tostAlert(intl.formatMessage(messages.invalid_source), 'error');
            return;
        }

        const source_id = source_data?.payload?.__auto_id__ ?? null;
        const source_name = source_data?.payload?.name ?? null;
        const source_connector = source_data?.payload?.source_type ?? null;

        const confirm = window.confirm(`${intl.formatMessage(messages.delete_check)}: ${source_name}?`);
        if (!confirm) return;

        setLoading(true);

        try {

            if (source_connector === "mongodb") {
                const deleteResponse = await AnalyticsApi.deleteSourceConnetion(source_id);
                if (deleteResponse.status !== 200) {
                    throw new Error('Failed to delete MongoDB connection');
                }
            }

            const updateResponse = await AnalyticsDataSourcesApi.delete(source_id);
            if (updateResponse.status === 200) {
                tostAlert(intl.formatMessage(messages.integration_remove), 'success');
                dispatch(setAnalyticsDataSourcesAdded(true))
            } else {
                throw new Error('Update request failed');
            }

        } catch (err) {
            console.error('Delete integration error:', err);
            if (err.status === 409) {
                const errorMessage = err?.response?.data?.detail || 'Conflict error occurred';
                tostAlert(errorMessage, 'error');
            } else {
                const errorMessage = err.message || 'Failed to remove Integration';
                tostAlert(errorMessage, 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const connectorNames = {
        google_drive: "Google Drive",
        one_drive: "One Drive",
        aws_s3: "AWS S3",
        azure_blob: "Azure Blob",
        mongodb: "Mongo DB",
        mysql: "Mysql",
        ameya_extract: "Ameya Extract",
        gmail: "Gmail",
        outlook: "Outlook",
        appflyte: "Appflyte"
    };

    const progressStatus = {
        connected: "Connected",
        in_progress: "In Progress",
        error: "Error",
    }

    const editSupportedConnectors = ['google_drive', 'one_drive', 'gmail', 'outlook']

    const columns = [
        {
            id: 'payload.name',
            label: 'Name',
            sortable: true,
            renderCell: (row) => row?.payload?.name
        },
        {
            id: 'payload.source_type',
            label: 'Connector',
            sortable: true,
            renderCell: (row) =>
                <Box display={'flex'} alignItems={'center'}>
                    <span style={{ marginRight: '10px' }}>
                        <ReactSVG
                            src={{
                                google_drive: IconSvg.driveIcon,
                                one_drive: IconSvg.onedriveIcon,
                                aws_s3: IconSvg.awsIcon,
                                azure_blob: IconSvg.azureBlobIcon,
                                mongodb: IconSvg.mongoIcon,
                                mysql: IconSvg.mysqlIcon,
                                ameya_extract: IconSvg.ameyaIcon,
                                gmail: IconSvg.mailIcon,
                                outlook: IconSvg.mailIcon,
                                appflyte: IconSvg.aiQmsIcon
                            }[row?.payload?.source_type]}
                            beforeInjection={(svg) => {
                                svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                                svg.setAttribute('stroke', '#0B51C5');
                            }}
                        />
                    </span>
                    <span>{connectorNames[row?.payload?.source_type] || row?.payload?.source_type}</span>
                </Box>
        },
        {
            id: 'payload.status',
            label: 'Status',
            sortable: true,
            renderCell: (row) =>
                <Box>
                    {row?.payload?.status === "in_progress" ?
                        (<Box display={'flex'} alignItems={'center'}>
                            <Tooltip
                                title={
                                    <Box display="flex" flexDirection="column">
                                        <span>Mongo Index Progress: {row?.payload?.mongoIndexProgress || 0}%</span>
                                        <span>Mongo FieldIndex Progress: {row?.payload?.mongoFieldIndexProgress || 0}%</span>
                                    </Box>
                                }
                                arrow  >
                                <Box display={'flex'} alignItems={'center'}>
                                    <span style={{ marginRight: '10px' }}>
                                        <ReactSVG
                                            src={IconSvg.trainingIcon}
                                            beforeInjection={(svg) => {
                                                svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                                            }}
                                        />
                                    </span>
                                    <span>{progressStatus[row?.payload?.status] || row?.payload?.status}</span>
                                </Box>
                            </Tooltip>
                        </Box>)
                        :
                        <Box display={'flex'} alignItems={'center'}>
                            <span style={{ marginRight: '10px' }}>
                                <ReactSVG
                                    src={{
                                        connected: IconSvg.trainedIcon,
                                        failed: IconSvg.errorIcon,
                                    }[row?.payload?.status]}
                                    beforeInjection={(svg) => {
                                        svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                                    }}
                                />
                            </span>
                            <span>{progressStatus[row?.payload?.status] || row?.payload?.status}</span>
                        </Box>
                    }
                </Box>
        },
        {
            id: 'payload.auth_status',
            label: 'Auth Status',
            sortable: true,
            renderCell: (row) => {
                const status = row?.payload?.auth_status ?? "valid"
                const formatted = status.charAt(0).toUpperCase() + status.slice(1);
                return (
                    <Box display={'flex'} alignItems={'center'}>
                        <span style={{ marginRight: '10px' }}>
                            <ReactSVG
                                src={{
                                    Valid: IconSvg.trainedIcon,
                                    Expired: IconSvg.errorIcon,
                                }[formatted]}
                                beforeInjection={(svg) => {
                                    svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                                }}
                            />
                        </span>
                        <span>{formatted}</span>
                    </Box>
                );
            }
        },
        {
            id: 'payload.created_on',
            label: 'Created on',
            sortable: true,
            renderCell: (row) => moment(row?.payload?.created_on)?.format('DD-MMM-YYYY hh:mm A')
        },
        {
            id: '',
            label: '',
            sortable: false,
            renderCell: (row) =>
                <Box display="flex" alignItems="center">

                    {editSupportedConnectors?.some(connector => row?.payload?.source_type === connector)
                        ?
                        (<span
                            style={{ marginRight: '10px' }}
                            onClick={() => setModalData({ ...modalData, modalstatus: true, integration_data: row })}
                        >
                            <ReactSVG
                                src={IconSvg.editIcon}
                                beforeInjection={(svg) => {
                                    svg.setAttribute('style', 'width:20px; height:20px; display:block;');
                                    svg.setAttribute('stroke', '#0B51C5');
                                }}
                            />
                        </span>)
                        :
                        (<span style={{ width: '20px', height: '20px', marginRight: '10px' }} />)}

                    <span onClick={() => handleIntegrationsDelete(row)}>
                        <ReactSVG
                            src={IconSvg.deleteIcon}
                            beforeInjection={(svg) => {
                                svg.setAttribute('style', 'width:20px; height:20px; display:block;');
                                svg.setAttribute('stroke', '#C30E2E');
                            }}
                        />
                    </span>
                </Box>

        }
    ];

    const handleModalClose = () => {
        setModalData(modalDetails);
    }

    const typeConfig = {
        google_drive: { icon: IconSvg.driveIcon, label: "Google Drive" },
        one_drive: { icon: IconSvg.onedriveIcon, label: "Microsoft OneDrive" },
        gmail: { icon: IconSvg.mailIcon, label: "Gmail" },
        outlook: { icon: IconSvg.mailIcon, label: "Outlook" }
    };
    const currentType = typeConfig[modalData?.integration_data?.payload?.source_type];

    const waitForPopupMessage = (accept) => {
        return new Promise((resolve) => {
            const handler = (event) => {
                if (accept(event.data)) {
                    window.removeEventListener('message', handler);
                    resolve(event.data);
                }
            };
            window.addEventListener('message', handler);
        });
    };

    const handleConnectionIntegration = async () => {
        setAuthLoading(true);
        try {
            try {
                setModalData({ ...modalData, modalstatus: false });

                const subscriptionId = await fetchSubscriptionId();
                const appName = window.__ENV__.APP_NAME;
                const origin_url = window.__ENV__.AMEYA_WEB_URL;
                const auth_server_url = window.__ENV__.APPFLYTE_AUTHENTICATION_URL;
                const integration_type = modalData?.integration_data?.payload?.source_type;

                const authMap = { gmail: "google", google_drive: "google", outlook: "azure", one_drive: "azure" };
                const connection_auth_type = authMap[integration_type] || "azure";

                const serviceTypeMap = { google_drive: "gdrive", one_drive: "onedrive", gmail: "gmail", outlook: "outlook" };
                const service_type = serviceTypeMap[integration_type];

                const response = await fetch(`${auth_server_url}/ameya_service/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        provider: connection_auth_type,
                        subscription_id: subscriptionId,
                        app_name: appName,
                        origin_url: origin_url,
                        service_type: service_type
                    }),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                const { auth_url } = data;

                if (!auth_url) {
                    throw new Error('No auth URL received from server');
                }

                const popup = window.open(auth_url, 'oauth_popup', 'width=600,height=600');
                if (!popup) {
                    throw new Error('Popup was blocked. Please allow popups for this site.');
                }

                popup.focus();

                const popupMessagePromise = waitForPopupMessage((d) => d?.source === 'oauth-popup' && typeof d.user_id === 'string');
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('OAuth timeout')), 60000);
                });

                const popupClosedPromise = new Promise((_, reject) => {
                    const checkClosed = setInterval(() => {
                        if (popup.closed) {
                            clearInterval(checkClosed);
                            reject(new Error('OAuth popup was closed'));
                        }
                    }, 1000);
                });

                const { user_id, user_mail, refresh_token } = await Promise.race([
                    popupMessagePromise,
                    timeoutPromise,
                    popupClosedPromise
                ]);

                if (!popup.closed) {
                    popup.close();
                }

                await handleConnectionAuth(user_id, user_mail, refresh_token);

            } catch (error) {
                console.error('OAuth integration error:', error);

                if (error.message.includes('Popup was blocked')) {
                    tostAlert(intl.formatMessage(messages.pop_up_block), 'error');
                } else if (error.message.includes('OAuth timeout')) {
                    tostAlert(intl.formatMessage(messages.oauth_timeout), 'error');
                } else if (error.message.includes('OAuth popup was closed')) {
                    tostAlert(intl.formatMessage(messages.oauth_cancel), 'info');
                } else {
                    tostAlert(intl.formatMessage(messages.connection_error), 'error');
                }
                setModalData({ ...modalData, modalstatus: true });
            }
        } catch (error) {
            console.error('Integration error:', error);
            tostAlert(intl.formatMessage(messages.unexpected_error), 'error');
            setModalData({ ...modalData, modalstatus: true });
        } finally {
            setAuthLoading(false);
        }
    };

    const handleConnectionAuth = async (user_id, user_mail, refresh_token) => {
        if (loading) return;
        setLoading(true);
        try {

            const integration_id = modalData?.integration_data?.payload?.__auto_id__;
            const update_key = modalData?.integration_data?.update_key

            const reqObj = {
                "id": integration_id,
                "fields": [
                    { "path": "$.user_id", "value": user_id },
                    { "path": "$.email", "value": user_mail },
                    { "path": "$.refresh_token", "value": refresh_token },
                    { "path": "$.auth_status", "value": "valid" },
                    { "path": "$.updated_on", "value": moment().format('YYYY-MM-DD HH:mm:ss') }
                ]
            }

            const response = await AnalyticsDataSourcesApi.update(JSON.stringify(reqObj), integration_id, update_key);
            if (response.status === 200) {
                tostAlert(intl.formatMessage(messages.connection_success), 'success');
                dispatch(setAnalyticsDataSourcesAdded(true))
                handleModalClose();
            } else {
                tostAlert(intl.formatMessage(messages.connection_error), 'error');
            }

        } catch (error) {
            console.error('Connection auth error:', error);
            tostAlert(intl.formatMessage(messages.connection_error), 'error');
        } finally {
            setLoading(false);
        }
    }

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>
                {(loading || authLoading) && <LoadBar />}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    <Typography sx={styles.mainHeadingText}>Integrations</Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            onClick={() => navigate('/sources/analytics-tool/connectors')}
                            sx={{ backgroundColor: '#0B51C5', borderRadius: '20px', width: '160px' }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                                ADD INTEGRATION
                            </Typography>
                        </Button>
                    </Box>
                </Box>

                <Box marginTop={'30px'} paddingBottom={'20px'}>
                    <GeneralTable
                        data={connectors}
                        columns={columns}
                        loading={loading}
                    />
                </Box>

            </Box>


            <Modal open={modalData.modalstatus} onClose={handleModalClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box sx={mainStyles.modalcontainer}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {currentType && <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <ReactSVG
                                src={currentType.icon}
                                beforeInjection={(svg) => {
                                    svg.setAttribute("style", "width: 24px; height:24px; display: block;");
                                    svg.setAttribute("stroke", "#0B51C5");
                                }}
                            />
                            <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>
                                {currentType.label}
                            </Typography>
                        </Box>}
                        <IconButton onClick={handleModalClose}>
                            <Close />
                        </IconButton>
                    </Box>
                    <Box marginTop={'10px'}>
                        <Typography sx={{ fontSize: '14px', fontWeight: '600', marginBottom: '5px' }}>Integration Name</Typography>
                        <TextField
                            size='small'
                            fullWidth
                            disabled
                            value={modalData?.integration_data?.payload?.name}
                        />
                    </Box>
                    <Box sx={{ marginTop: '25px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Button
                            onClick={() => handleConnectionIntegration()}
                            sx={styles.integrateBtn}
                            disabled={authLoading || loading}
                        >
                            <Typography sx={styles.integrateBtnText}>Re Connect</Typography>
                        </Button>
                    </Box>
                </Box>
            </Modal>

        </Box>
    )
}

export default AnalyticsIntegrations;